import React, { useState } from 'react';
import {
    Box, Button, CircularProgress, Divider, Paper,
    Typography, List, ListItem, ListItemIcon, ListItemText,
    Alert,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShieldIcon from '@mui/icons-material/Shield';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { swotService, SwotAnaliseIa } from '../services/SwotService';

const SwotAiAnalise: React.FC = () => {
    const [analise, setAnalise] = useState<SwotAnaliseIa | null>(null);
    const [loading, setLoading] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    const gerarAnalise = async () => {
        setLoading(true);
        setErro(null);
        try {
            const resultado = await swotService.gerarAnaliseIa();
            setAnalise(resultado);
        } catch (e: any) {
            setErro(e?.response?.data?.message || e.message || 'Erro ao gerar análise.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoAwesomeIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">Análise Estratégica com IA</Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                    onClick={gerarAnalise}
                    disabled={loading}
                >
                    {loading ? 'Analisando...' : analise ? 'Reanalisar' : 'Gerar análise'}
                </Button>
            </Box>

            {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

            {!analise && !loading && (
                <Typography variant="body2" color="text.secondary">
                    Clique em "Gerar análise" para obter insights estratégicos baseados na sua matriz SWOT.
                </Typography>
            )}

            {analise && !loading && (
                <Box>
                    {analise.insight && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                mb: 3,
                                backgroundColor: analise.cenario === 'FAVORAVEL'
                                    ? 'rgba(46, 125, 50, 0.08)'
                                    : 'rgba(211, 47, 47, 0.08)',
                                borderLeft: `4px solid ${analise.cenario === 'FAVORAVEL' ? '#2e7d32' : '#d32f2f'}`,
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1.5,
                            }}
                        >
                            <LightbulbIcon sx={{ color: analise.cenario === 'FAVORAVEL' ? '#2e7d32' : '#d32f2f', mt: 0.3 }} />
                            <Typography variant="body1" fontStyle="italic">
                                {analise.insight}
                            </Typography>
                        </Paper>
                    )}

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Resumo Estratégico
                        </Typography>
                        <Box sx={{ '& p': { mb: 1 }, '& strong': { fontWeight: 600 } }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {analise.resumoEstrategico}
                            </ReactMarkdown>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
                        {analise.estrategiasSO.length > 0 && (
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <TrendingUpIcon color="success" fontSize="small" />
                                    <Typography variant="subtitle2" fontWeight="bold" color="success.main">
                                        Estratégias SO — Alavancar forças
                                    </Typography>
                                </Box>
                                <List dense disablePadding>
                                    {analise.estrategiasSO.map((item, i) => (
                                        <ListItem key={i} disableGutters sx={{ alignItems: 'flex-start' }}>
                                            <ListItemIcon sx={{ minWidth: 28, mt: 0.3 }}>
                                                <CheckCircleOutlineIcon fontSize="small" color="success" />
                                            </ListItemIcon>
                                            <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}

                        {analise.estrategiasWT.length > 0 && (
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <ShieldIcon color="error" fontSize="small" />
                                    <Typography variant="subtitle2" fontWeight="bold" color="error.main">
                                        Estratégias WT — Minimizar riscos
                                    </Typography>
                                </Box>
                                <List dense disablePadding>
                                    {analise.estrategiasWT.map((item, i) => (
                                        <ListItem key={i} disableGutters sx={{ alignItems: 'flex-start' }}>
                                            <ListItemIcon sx={{ minWidth: 28, mt: 0.3 }}>
                                                <CheckCircleOutlineIcon fontSize="small" color="error" />
                                            </ListItemIcon>
                                            <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}
                    </Box>

                    {analise.topRecomendacoes.length > 0 && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                Top Recomendações
                            </Typography>
                            <List dense disablePadding>
                                {analise.topRecomendacoes.map((item, i) => (
                                    <ListItem key={i} disableGutters sx={{ alignItems: 'flex-start' }}>
                                        <ListItemIcon sx={{ minWidth: 32, mt: 0.3 }}>
                                            <Box
                                                sx={{
                                                    width: 22, height: 22, borderRadius: '50%',
                                                    backgroundColor: 'primary.main',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: 'white', fontSize: 11, fontWeight: 'bold',
                                                }}
                                            >
                                                {i + 1}
                                            </Box>
                                        </ListItemIcon>
                                        <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    )}
                </Box>
            )}
        </Paper>
    );
};

export default SwotAiAnalise;
